// Z80 Phase 1: Customer Pricing System -- shared types, mirroring the schema in
// supabase/migrations/20260916b_z80_pricing_schema.sql exactly (field names/nullability).

export type PricingUnit =
  | 'per_container' | 'per_kg' | 'per_cbm' | 'per_parcel' | 'per_km' | 'flat'

export type RateLineUnit = PricingUnit | 'per_pallet' | 'per_day' | 'per_shipment' | 'per_entry' | 'percentage'
  // 20261003: per_wm = greater of weight / volume, per_week, per_heading = number of HS headings
  | 'per_wm' | 'per_week' | 'per_heading'

// 20261003: what a line is priced against. Only DELIVERY depends on the delivery method.
export type RateLineScope = 'CONTAINER' | 'HBL' | 'CARGO' | 'DELIVERY'
export type PeriodUnit = 'day' | 'week'
export type FreeUnit = 'day' | 'week' | 'each'

// The columns 20261003 added to pricing_rate_lines AND supplier_rate_lines (identical on both sides).
export interface RateLineFlags {
  scope: RateLineScope
  period_unit: PeriodUnit | null
  free_qty: number | null
  free_unit: FreeUnit | null
  is_at_cost: boolean // pass-through: no unit price, the amount comes from the real data source
  is_optional: boolean // only charged when the customer asks
  is_enabled: boolean // switched off instead of deleted, so every card keeps the same shape
}

export type RateCardStatus = 'DRAFT' | 'ACTIVE' | 'EXPIRED' | 'ARCHIVED'

export interface PricingCategory {
  id: string
  code: string
  name_en: string
  name_zh: string | null
  default_pricing_unit: PricingUnit
  description: string | null
  display_order: number
  is_active: boolean
}

export interface PricingLane {
  id: string
  name: string
  category_id: string
  origin_port_id: string | null
  destination_port_id: string | null
  origin_city: string | null
  destination_city: string | null
  is_active: boolean
}

// Shared shape of pricing_clearance_types / pricing_delivery_methods (20260918b): flat lookups
// whose applicable_modes holds pricing_categories.code values (e.g. {SEA,AIR}).
export interface PricingModeScopedOption {
  id: string
  code: string
  name: string
  name_zh: string | null
  applicable_modes: string[]
  is_active: boolean
}

export interface PricingChargeCode {
  id: string
  // 20261001: the job_charges-layer code this maps to (charge_codes.id). Optional so the Charge
  // Codes tab keeps working before that migration has been run (select('*') just won't return it).
  charge_code_id?: string | null
  code: string
  name_en: string
  name_zh: string | null
  description: string | null
  default_sage_ledger: string | null
  default_sage_department: string | null
  taxable: boolean
  is_active: boolean
}

export interface PricingRateCard {
  id: string
  customer_id: string | null
  mode_id: string // FK to pricing_categories (repurposed as Mode: SEA/AIR/RAIL/TRUCK), renamed from category_id by 20260918b
  clearance_type_id: string | null
  delivery_method_id: string | null // NULL since 20261003: only required when the card has DELIVERY-scope lines
  lane_id: string | null
  rate_card_name: string
  effective_from: string
  effective_to: string | null
  currency: string
  status: RateCardStatus
  created_by: string | null
  created_at: string
  modified_at: string
  is_public_to_customer: boolean
  notes: string | null
}

export interface PricingRateLine extends RateLineFlags {
  id: string
  rate_card_id: string
  charge_code_id: string
  unit: RateLineUnit
  container_type_id: string | null
  unit_price: number | null // NULL = not priced yet, or at-cost (20261003)
  min_charge: number | null
  max_charge: number | null
  notes: string | null
}

export interface PricingRateLineCost {
  rate_line_id: string
  unit_cost: number
}

export interface PricingRateTier {
  id: string
  rate_line_id: string
  tier_from: number
  tier_to: number | null
  unit_price: number
}

export const PRICING_UNIT_OPTIONS: PricingUnit[] = [
  'per_container', 'per_kg', 'per_cbm', 'per_parcel', 'per_km', 'flat',
]

export const RATE_LINE_UNIT_OPTIONS: RateLineUnit[] = [
  'per_container', 'per_kg', 'per_cbm', 'per_parcel', 'per_pallet', 'per_km', 'flat', 'per_day',
  // widened by 20260918_z80_pricing_line_route_units.sql -- the DB CHECK has allowed these for a while
  'per_shipment', 'per_entry', 'percentage',
  'per_wm', 'per_week', 'per_heading', // 20261003
]

export const RATE_CARD_STATUS_OPTIONS: RateCardStatus[] = ['DRAFT', 'ACTIVE', 'EXPIRED', 'ARCHIVED']

// Z81: supplier (cost-side) rate cards -- mirrors supabase/migrations/20261001b_supplier_rate_cards.sql.
export interface SupplierRateCard {
  id: string
  supplier_id: string // customers.id where is_supplier = true
  mode_id: string
  clearance_type_id: string | null
  delivery_method_id: string | null // NULL since 20261003
  lane_id: string | null
  rate_card_name: string
  effective_from: string
  effective_to: string | null
  currency: string
  status: RateCardStatus
  created_by: string | null
  created_at: string
  modified_at: string
  notes: string | null
}

export interface SupplierRateLine extends RateLineFlags {
  id: string
  rate_card_id: string
  charge_code_id: string
  unit: RateLineUnit
  container_type_id: string | null
  postcode_prefix: string | null // upper-case alphanumeric, NULL = the supplier's fallback price
  unit_price: number | null // NULL = not priced yet, or at-cost (20261003)
  min_charge: number | null
  max_charge: number | null
  notes: string | null
}

export const RATE_LINE_SCOPE_OPTIONS: { value: RateLineScope; label: string; hint: string }[] = [
  { value: 'CONTAINER', label: 'Container', hint: 'Once per container' },
  { value: 'HBL', label: 'HBL', hint: 'Once per house B/L' },
  { value: 'CARGO', label: 'Cargo', hint: 'By quantity x time (storage, scanning)' },
  { value: 'DELIVERY', label: 'Delivery', hint: 'Per delivery -- the only scope priced by delivery method' },
]

// 20261003: a "standard quote" skeleton. Holds the shape of a quote, never a price.
export interface QuoteTemplate {
  id: string
  code: string
  name: string
  name_zh: string | null
  mode_id: string
  clearance_type_id: string | null
  description: string | null
  is_active: boolean
}
